import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Loader2, Sparkles, X, Zap } from "lucide-react";
import BondNumbersStageVisual from "./BondNumbersStageVisual";
import BondNumbersPickSelector from "./BondNumbersPickSelector";
import BondPlayReservedTicket from "../bond-play/BondPlayReservedTicket";
import {
  bondNumbersCancel,
  bondNumbersReserve,
  bondNumbersReveal,
  clearBondNumbersLastSettled,
  clearBondNumbersPendingRound,
  fetchBondNumbersConstants,
  fetchBondNumbersLanding,
  selectBondNumbersActionLoading,
  selectBondNumbersConstants,
  selectBondNumbersLastSettled,
  selectBondNumbersPendingRound,
} from "../../redux/bondNumbersSlice";
import { patchAppUser } from "../../redux/appAuthSlice";
import {
  computeBondNumbersQuote,
  formatBondNumbersCoin,
  formatOddsMultiplier,
  numberLabel,
  pickLabel,
  resolveBondNumbersSettledRound,
} from "../../utils/bondNumbersHelpers";
import { usePendingPlayCountdown } from "../../hooks/usePendingPlayCountdown";
import useBondDiceResultDwell from "../../hooks/useBondDiceResultDwell";
import useRevealAnimation from "../../hooks/useRevealAnimation";

const BondNumbersStage = ({ isAuthenticated, userCoin, onSignIn, onNotify }) => {
  const dispatch = useDispatch();
  const constants = useSelector(selectBondNumbersConstants);
  const pendingRound = useSelector(selectBondNumbersPendingRound);
  const lastSettled = useSelector(selectBondNumbersLastSettled);
  const actionLoading = useSelector(selectBondNumbersActionLoading);

  const [selectedPick, setSelectedPick] = useState(null);
  const [stakeInput, setStakeInput] = useState("500");
  const [revealTicket, setRevealTicket] = useState(null);
  const {
    isRevealing,
    revealedRound,
    resetReveal,
    finishReveal,
    runReveal,
  } = useRevealAnimation();

  const stake = useMemo(() => Math.floor(Number(stakeInput) || 0), [stakeInput]);
  const minStake = constants?.minStake || 100;
  const maxStake = constants?.maxStake || 500000;
  const playTtlMs = constants?.pendingPlayTtlMs || 120 * 1000;
  const odds = constants?.odds || 2;
  const hasPending = pendingRound?.status === "Pending";
  const visualPendingRound = hasPending ? pendingRound : isRevealing ? revealTicket : null;
  const showPending = !!visualPendingRound;

  const handleTicketExpired = useCallback(() => {
    resetReveal();
    setRevealTicket(null);
    setSelectedPick(null);
    const stake = Number(pendingRound?.stake || 0);
    dispatch(clearBondNumbersPendingRound());
    dispatch(fetchBondNumbersLanding());
    if (stake > 0) {
      dispatch(patchAppUser({ COIN: Math.max(0, userCoin + stake) }));
    }
    onNotify?.({
      variant: "refund",
      game: "numbers",
      title: "Ticket expired",
      subtitle: "Cancel to refund your stake.",
      amount: stake,
      autoCloseMs: 3500,
    });
  }, [dispatch, onNotify, pendingRound?.stake, resetReveal, userCoin]);

  const { secondsLeft, expired: ticketExpired } = usePendingPlayCountdown({
    active: showPending,
    playExpiresAt: visualPendingRound?.playExpiresAt,
    createdAt: visualPendingRound?.createdAt,
    ttlMs: playTtlMs,
    onExpired: handleTicketExpired,
  });

  useEffect(() => {
    if (!constants) dispatch(fetchBondNumbersConstants());
  }, [constants, dispatch]);

  const ticketRound = revealTicket || pendingRound;

  const resolvedRound = useMemo(
    () =>
      resolveBondNumbersSettledRound({
        revealedRound,
        lastSettled,
        ticketRound,
      }),
    [revealedRound, lastSettled, ticketRound]
  );

  useEffect(() => {
    if (isRevealing && resolvedRound) finishReveal(resolvedRound);
  }, [isRevealing, resolvedRound, finishReveal]);

  const handleResultCleared = useCallback(() => {
    resetReveal();
    setSelectedPick(null);
    dispatch(clearBondNumbersLastSettled());
  }, [dispatch, resetReveal]);

  const displaySettledRound = useBondDiceResultDwell(resolvedRound, { onCleared: handleResultCleared });
  const resultRound = displaySettledRound ?? resolvedRound;
  const isRevealingUi = Boolean(ticketRound && !resolvedRound && (isRevealing || actionLoading));
  const showPendingUi = showPending && !resolvedRound;

  const stageRound = showPendingUi ? visualPendingRound : resultRound;
  const stageNumbers = stageRound?.numbers || [];
  const stageReference = stageRound?.referenceNumber ?? null;
  const stageOutcome = resultRound?.outcome ?? null;

  const liveQuote = useMemo(
    () => computeBondNumbersQuote(stake, constants),
    [constants, stake]
  );

  const canReserve =
    isAuthenticated &&
    !showPendingUi &&
    !isRevealingUi &&
    stake >= minStake &&
    stake <= maxStake &&
    stake <= userCoin;

  const handleReserve = async () => {
    try {
      await dispatch(bondNumbersReserve({ stake })).unwrap();
      resetReveal();
      setRevealTicket(null);
      setSelectedPick(null);
      onNotify?.({
        variant: "ticket",
        game: "numbers",
        title: "Ticket locked in",
        subtitle: "Your 11 numbers are ready — call Higher or Lower.",
        stake,
        maxWin: liveQuote?.maxWin,
        autoCloseMs: 4000,
      });
    } catch (error) {
      onNotify?.(typeof error === "string" ? error : "Could not reserve ticket");
    }
  };

  const handleReveal = async () => {
    if (!pendingRound?._id || !selectedPick) {
      onNotify?.("Pick Higher or Lower first.");
      return;
    }
    setRevealTicket(pendingRound);
    try {
      const round = await runReveal(() =>
        dispatch(
          bondNumbersReveal({ roundId: pendingRound._id, playerPick: selectedPick })
        ).unwrap()
      );
      setRevealTicket(null);
      const won = round?.status === "Won";
      const net = Number(round?.playerNetPayout || 0);
      const pick = pickLabel(selectedPick);
      onNotify?.(
        won
          ? {
              variant: "win",
              game: "numbers",
              title: "You called it!",
              subtitle: `${pick} — reveal landed on ${numberLabel(round.outcome)}.`,
              pick,
              outcome: numberLabel(round.outcome),
              stake: Number(round?.stake || pendingRound?.stake || 0),
              netPayout: net,
            }
          : {
              variant: "loss",
              game: "numbers",
              title: "Close one",
              subtitle: `Reveal ${numberLabel(round.outcome)} — ${pick} missed.`,
              pick,
              outcome: numberLabel(round.outcome),
              stake: Number(round?.stake || pendingRound?.stake || 0),
            }
      );
    } catch (error) {
      onNotify?.(typeof error === "string" ? error : "Reveal failed");
    }
  };

  const handleCancel = async () => {
    if (!pendingRound?._id) return;
    try {
      await dispatch(
        bondNumbersCancel({ roundId: pendingRound._id, stake: Number(pendingRound.stake || 0) })
      ).unwrap();
      resetReveal();
      setRevealTicket(null);
      setSelectedPick(null);
      onNotify?.({
        variant: "refund",
        game: "numbers",
        title: "Ticket canceled",
        subtitle: "Your stake is back in your wallet.",
        amount: Number(pendingRound.stake || 0),
        autoCloseMs: 3500,
      });
    } catch (error) {
      onNotify?.(typeof error === "string" ? error : "Cancel failed");
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="bcoin-empty">
        <p>Sign in to play Bond Numbers.</p>
        <button type="button" className="bcoin-btn bcoin-btn-primary" onClick={onSignIn}>
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div className="bcoin-stage bnums-stage">
      <header className="bcoin-head">
        <div>
          <p className="bcoin-eyebrow">
            Investor-backed · COIN only · {formatOddsMultiplier(odds)} odds · 11-number window
          </p>
          <h1 className="bcoin-title">Bond Numbers</h1>
        </div>
        <span
          className={`bcoin-status ${
            showPendingUi ? "bcoin-status-pending" : isRevealingUi ? "bcoin-status-pending" : "bcoin-status-muted"
          }`}
        >
          {showPendingUi ? "Reserved" : isRevealingUi ? "Revealing" : "Ready"}
        </span>
      </header>

      <BondNumbersStageVisual
        numbers={stageNumbers}
        referenceNumber={stageReference}
        outcome={stageOutcome}
        revealing={isRevealingUi}
      />

      {resultRound?.outcome != null ? (
        <p className="bcoin-round-outcome">
          Revealed <strong>{numberLabel(resultRound.outcome)}</strong>
          {resultRound.playerPick ? (
            <>
              {" "}
              · Your pick: {pickLabel(resultRound.playerPick)}
              {resultRound.status === "Won" ? (
                <> · Won {formatBondNumbersCoin(resultRound.playerNetPayout || 0)} COIN</>
              ) : null}
            </>
          ) : null}
        </p>
      ) : (
        <p className="bcoin-round-outcome bcoin-round-outcome-placeholder" aria-hidden>
          &nbsp;
        </p>
      )}

      {showPendingUi ? (
        <>
          <BondPlayReservedTicket
            variant="dice"
            actionVerb="Reveal"
            pickLabel={selectedPick ? pickLabel(selectedPick) : "Pick a side"}
            stakeLabel={`${formatBondNumbersCoin(visualPendingRound?.stake)} COIN`}
            maxWinLabel={`${formatBondNumbersCoin(visualPendingRound?.maxPotentialPayout)} COIN`}
            playExpiresAt={visualPendingRound?.playExpiresAt}
            createdAt={visualPendingRound?.createdAt}
            ttlMs={playTtlMs}
            secondsLeft={secondsLeft}
            expired={ticketExpired}
          />
          <BondNumbersPickSelector
            value={selectedPick}
            onChange={setSelectedPick}
            disabled={isRevealingUi || ticketExpired}
          />
        </>
      ) : (
        <>
          <p className="bnums-idle-copy">Reserve a ticket to draw your 11 numbers, then call Higher or Lower.</p>
          <div className="bcoin-join-row bcoin-pool-join-row">
            <label className="bcoin-stake-field">
              Stake (min {formatBondNumbersCoin(minStake)})
              <input
                type="number"
                min={minStake}
                max={maxStake}
                step={50}
                value={stakeInput}
                onChange={(event) => setStakeInput(event.target.value)}
              />
            </label>
          </div>
          {liveQuote ? (
            <p className="bcoin-share-preview">
              {formatOddsMultiplier(liveQuote.odds)} · Max win {formatBondNumbersCoin(liveQuote.maxWin)} COIN · Pool
              reserve {formatBondNumbersCoin(liveQuote.reserve)} COIN
            </p>
          ) : null}
        </>
      )}

      <div className="bcoin-flip-actions">
        {showPendingUi ? (
          <>
            <button
              type="button"
              className="bcoin-btn"
              disabled={actionLoading || isRevealingUi}
              onClick={handleCancel}
            >
              <X size={14} />
              Cancel
            </button>
            <button
              type="button"
              className="bcoin-btn bcoin-btn-primary"
              disabled={actionLoading || isRevealingUi || ticketExpired || !selectedPick}
              onClick={handleReveal}
            >
              {isRevealingUi ? <Loader2 size={14} className="bcoin-spin" /> : <Sparkles size={14} />}
              {isRevealingUi ? "Revealing…" : "Reveal"}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="bcoin-btn bcoin-btn-primary bcoin-flip-buy"
            disabled={actionLoading || !canReserve}
            onClick={handleReserve}
          >
            {actionLoading ? <Loader2 size={14} className="bcoin-spin" /> : <Zap size={14} />}
            <span className="bcoin-reserve-copy">
              <span>{actionLoading ? "Reserving…" : "Reserve ticket"}</span>
              {liveQuote?.maxWin && !actionLoading ? (
                <span className="bcoin-reserve-potential">
                  Win up to {formatBondNumbersCoin(liveQuote.maxWin)} COIN
                </span>
              ) : null}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default BondNumbersStage;
